<template>
  <div class="row receiver-sector__address-info g-2" v-if="hasHome">
    <el-divider />

    <AccountAddressModel 
      v-if="showUads" 
      ref="addressRef"
      v-model="uadsState" 
      type="address" 
      @ok="chooseAddress" 
    />

    <el-form-item v-if="(uadsState.uads_id === -1)" :label="showUads ? '' : t('orders.shippingAddress')" required>
      <el-row :gutter="10" style="width: 100%;">
        <el-col :xs="24" :sm="12" :md="6" class="address-select mb-3">
          <el-form-item prop="province">
            <el-select
              v-model="form.province" :disabled="Object.keys(provicesMap).length <= 0"
              :placeholder="`---${t('users.province')}---`"
              size="large"
              style="width: 100%;"
              @change="alertOuterIsland"
            >
              <el-option
                v-for="(obj, code) in provicesMap"
                :key="code"
                :label="obj.name + '(' + obj.name_en + ')'"
                :value="code"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="12" :md="6" class="address-select mb-3">
          <el-form-item prop="city">
            <el-select
              v-model="form.city"
              :disabled="Object.keys(cityMap).length <= 0"
              :placeholder="`---${t('users.city')}---`"
              size="large"
              style="width: 100%;"
              @change="alertOuterIsland"
            >
              <el-option
                v-for="(obj, code) in cityMap"
                :key="code"
                :label="obj.name + '(' + obj.name_en + ')'"
                :value="code"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col v-if="!!Object.keys(regionMap).length" :xs="24" :sm="12" :md="6" class="address-select mb-3">
          <el-form-item prop="region">
            <el-select
              v-model="form.region"
              :disabled="Object.keys(regionMap).length <= 0"
              :placeholder="`---${t('users.region')}---`"
              size="large"
              style="width: 100%;"
            >
              <el-option
                v-for="(obj, code) in regionMap"
                :key="code"
                :label="obj.name + '(' + obj.name_en + ')'"
                :value="code"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="12" :md="6" class="address-select mb-3">
          <el-form-item prop="postal_code">
            <el-input v-model="form.postal_code" :placeholder="t('users.postcode')" size="large" maxlength="20" />
          </el-form-item>
        </el-col>
        <el-col :span="24" class="address-select mb-3">
          <el-form-item prop="address">
            <el-input
              v-model="form.address"
              :placeholder="t('errors.pleaseInput', [t('orders.toAddress')])"
              size="large"
              maxlength="100"
              show-word-limit
            />
          </el-form-item>
          <p class="hint mt-3">{{ t("orders.addressHint") }}</p>
        </el-col>
      </el-row>
    </el-form-item>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVModel, useVModels } from '@vueuse/core';
import { notificationErrors } from '../../utils';
import { _api_getAddresses, _api_postAddress } from '../../api/index.js';
import AccountAddressModel from './AccountAddressModel.vue';

const props = defineProps({
  modelValue: Object,
  state: Object,
  proviceMap: Object,
  usedLogisticTypes: Array,
  isEdit: Boolean,
  showUads: Boolean,
})
const emit = defineEmits(['update:modelValue'])

const { t, locale } = useI18n()
const form = useVModel(props, 'modelValue', emit)
const { state } = useVModels(props, emit)

const twOuterIslands = [
  "ILA.DYI","KHH.DSA","KHH.NSA","PEN.MGG","PEN.WAN","PEN.QMI","PEN.BSA",
  "PEN.HXI","PEN.XYU","KMN.LYU","KMN.WQU","KMN.JCG","KMN.JHU","KMN.JNG",
  "KMN.JSA","TTT.LDO","TTT.LYU","PIF.LQU","LCG.BGN","LCG.DYN","LCG.JGG",
  "LCG.NGN", "PEN.PEN", "KMN.KMN", "LCG.LCG"]
const logistic_types = ['PERSONALLY', 'TCAT', 'MUNETEX']

const hasHome = computed(() => {
  const target = props.usedLogisticTypes.find(t => chkLocation(t))
  // console.debug("hasHome: ", !!target, target)
  
  return !!target;
});
const provicesMap = computed(() => {
  if (props.proviceMap) {
    uadsState.isOuterIsland = isOuterIsland.value
    if (isOuterIsland.value) {
      return props.proviceMap
    } else {
      const proviceMap = _.cloneDeep(props.proviceMap)
      delete proviceMap.PEN
      delete proviceMap.KMN
      delete proviceMap.LCG
      return proviceMap
    }
  }
  return {};
});
const cityMap = computed(() => {
  if (form.value.province) {
    uadsState.isOuterIsland = isOuterIsland.value
    const province_info = props.proviceMap[form.value.province];
    // console.log("province_info: ", province_info)
    if (!province_info) {
      return {}
    }
    const postal_code = province_info.postal;
    if (isEdit.value) {
      isEdit.value = false
    } else if (!(form.value.city in province_info.city)) {
      form.value.city = null
      form.value.postal_code = postal_code ? postal_code : null
    }
    if (isOuterIsland.value) {
      return province_info.city || {};
    } else {
      const cities = _.cloneDeep(province_info.city)
      
      if (form.value.province === "KHH") {
        delete cities.DSA
        delete cities.NSA
      } else if (form.value.province === "PIF") {
        delete cities.LQU
      } else if (form.value.province === "ILA") {
        delete cities.DYI
      } else if (form.value.province === "TTT") {
        delete cities.LDO
        delete cities.LYU
      }

      return cities || {};
    }
  }
  return {};
});
const regionMap = computed(() => {
  if (form.value.city) {
    const city_info = cityMap.value[form.value.city]
    if (!city_info) {
      return {}
    }
    const postal_code = city_info.postal
    if (postal_code) {
      form.value.postal_code = postal_code
    }
    return city_info.region || {}
  }
  return {}
});
// const isCreate = computed(() => form.value.uads_address)
const isCreate = computed(() => uadsState.uads_id === -1)

const isEdit = ref(props.isEdit)
const addressRef = ref()

const chooseAddress = (choose = null) => {
  // console.log("chooseAddress: ", choose)
  form.value.province = null
  form.value.city = null
  form.value.region = null
  form.value.postal_code = null
  form.value.address = null
  form.value.to_name = form.value.buyer_name
  form.value.to_mobile = form.value.buyer_mobile
  form.value.uads_address = true
  isEdit.value = false
  state.value.showToInfo = false
  if (choose) {
    form.value.province = choose.iso_province
    form.value.city = choose.iso_city
    form.value.region = choose.iso_region
    form.value.postal_code = choose.postcode
    form.value.address = choose.address
    form.value.to_name = choose.to_name
    form.value.to_mobile = choose.to_mobile
    state.value.showToInfo = true
    
    form.value.uads_address = false
    isEdit.value = true

    alertOuterIsland(form.value.city)
  }
}
const logistic = ref(null)
const chkLocation = (logistic_type) => {
  const target = logistic_types.includes(logistic_type)
  if (target) { 
    logistic.value = logistic_type
    if (state.value?.isOuterIsland && !(logistic.value in state.value.isOuterIsland)) {
      state.value.isOuterIsland[logistic.value] = false
    }
  }
  return target
}
const handleUnchecked = () => {
  const carts = form.value.carts.filter((el) => !el.checked && el.is_excluded_outer_island)
  carts.forEach((cart) => {
    if (chkLocation(cart.logistic_type)) {
      cart.checked = false
      cart.is_valid = false
    }
  })
}
const alertOuterIsland = (val) => {
  if (form.value.country != 'TW' || !form.value?.carts) {
    return false
  }
  
  const key = form.value.province + "." + val
  // console.debug(form.value.province, form.value.city, key)
  if (!twOuterIslands.includes(key)) {
    // console.debug("alertOuterIsland: ", form.value)
    const carts = form.value.carts.filter((el) => !el.is_valid && el.is_excluded_outer_island)
    carts.forEach((cart) => {
      if (chkLocation(cart.logistic_type)) {
        cart.checked = true
        cart.is_valid = true
      }
    })
    state.value.isOuterIsland[logistic.value] = false
    return false
  }
  // console.debug("alertOuterIsland: ", key)
  state.value.isOuterIsland[logistic.value] = true
  // console.debug("alertOuterIsland: ", state.value.isOuterIsland)
  const cart_ids = []
  const items = []
  // 檢查購物車 is_excluded_outer_island
  const carts = form.value.carts.filter((el) => el.checked && el.is_excluded_outer_island)
  carts.forEach((cart) => {
    if (chkLocation(cart.logistic_type)) {
      // console.debug("cart: ", cart)
      items.push(cart.product_name)
      cart_ids.push(cart.cart_id)
    }
  })
  if (items.length) {
    swal.fire({
      title: '部分商品不支援離島寄送',
      html: items.join("<br />"),
      allowOutsideClick: false,
      showCancelButton: true,
      confirmButtonColor: 'var(--secondary_color)',
      confirmButtonText: '取消勾選外島商品',
      cancelButtonColor: 'var(--primary_color)',
      cancelButtonText: '重選地址',
    }).then((result) => {
      // console.debug("result: ", result)
      if (result.value) {
        console.debug("取消勾選外島商品")
        carts.forEach((cart) => {
          if (cart_ids.includes(cart.cart_id)) {
            cart.checked = false
            cart.is_valid = false
          }
        })
      } else if (result.dismiss == "cancel") {
        console.debug("重選地址")
        if (uadsState.uads_id === -1) {
          form.value.city = null
          form.value.postal_code = null
        } else {
          uadsState.uads_id = -1
          chooseAddress(uadsState.uads_id)
        }
        state.value.isOuterIsland[logistic.value] = false
      }
    })
    
    return true
  } else {
    handleUnchecked()
  }
  return false
}

const isOuterIsland = computed(() => {
  const target = props.usedLogisticTypes.find(val => chkLocation(val));
  if (!state.value?.logistics) {
    // 會員常用地址
    return true
  }
  const item = state.value?.logistics.find((el) => el.type === target)
  // console.debug("isOuterIsland", item)
  if (!item) return false
  const rate = item.rates.find((rate) => rate.countries.includes(form.value.country))
  if (rate?.fees.length) {
    return rate?.fees[0]?.is_open_outer_island
  }
  return false
})

const uadsState = reactive({
  uads_id: -1,
  isOuterIsland: false,
})

defineExpose({ addressRef, isEdit, isCreate, uadsState })
</script>
